"use client";

import { useEffect, useState } from "react";
import { Check, Loader2, MessageSquareText, Send } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import type { ProgressCard } from "@/lib/types";

export function ProgressSlackSyncButton({
  card,
  canWrite,
}: {
  card: ProgressCard;
  canWrite: boolean;
}) {
  const [binding, setBinding] = useState<{ id: string; enabled: boolean } | null>(
    null
  );
  const [loading, setLoading] = useState(Boolean(card.projectId));
  const [sending, setSending] = useState(false);
  const [notice, setNotice] = useState<{
    tone: "success" | "error";
    text: string;
  } | null>(null);

  useEffect(() => {
    let active = true;
    setNotice(null);
    if (!card.projectId) {
      setBinding(null);
      setLoading(false);
      return () => {
        active = false;
      };
    }
    setLoading(true);
    void fetch(
      `/api/workspaces/${encodeURIComponent(card.workspaceId)}/projects/${encodeURIComponent(card.projectId)}/slack-channel`,
      { cache: "no-store" }
    )
      .then(async (response) => {
        const data = (await response.json().catch(() => ({}))) as {
          binding?: { id: string; enabled: boolean } | null;
        };
        if (!response.ok) throw new Error();
        if (active) setBinding(data.binding ?? null);
      })
      .catch(() => {
        if (active) setBinding(null);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [card.projectId, card.workspaceId]);

  if (!canWrite || !card.projectId) return null;

  async function send() {
    if (!binding || !binding.enabled || card.status !== "published") return;
    setSending(true);
    setNotice(null);
    try {
      const response = await fetch(
        `/api/workspaces/${encodeURIComponent(card.workspaceId)}/progress-cards/${encodeURIComponent(card.id)}/slack-sync`,
        {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ binding_id: binding.id }),
        }
      );
      const data = (await response.json().catch(() => ({}))) as {
        message?: string;
        result?: { status?: string; action?: string };
      };
      if (!response.ok) {
        throw new Error(data.message || "Slack delivery failed.");
      }
      setNotice({
        tone: "success",
        text:
          data.result?.status === "unchanged"
            ? "Already current"
            : data.result?.action === "update"
              ? "Slack updated"
              : "Sent to Slack",
      });
    } catch (error) {
      setNotice({
        tone: "error",
        text:
          error instanceof Error ? error.message : "Slack delivery failed.",
      });
    } finally {
      setSending(false);
    }
  }

  if (loading) {
    return (
      <span className="inline-flex h-7 items-center px-2 text-[10.5px] text-[var(--fg-muted)]">
        <Loader2 size={11} className="animate-spin" />
      </span>
    );
  }

  if (!binding) {
    return (
      <span
        className="inline-flex h-7 items-center gap-1 px-2 text-[10.5px] text-[var(--fg-muted)]"
        title="Configure a Slack channel from the project overview"
      >
        <MessageSquareText size={11} />
        No channel
      </span>
    );
  }

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => void send()}
        disabled={sending || !binding.enabled || card.status !== "published"}
        title={
          card.status !== "published"
            ? "Publish this progress card before sending"
            : !binding.enabled
              ? "Slack updates are paused for this project"
              : "Sync this progress card to the project's Slack channel"
        }
        className="inline-flex h-7 items-center gap-1 rounded-md px-2 text-[11.5px] font-medium text-[var(--fg-secondary)] hover:bg-[var(--bg-hover)] hover:text-[var(--fg)] disabled:cursor-not-allowed disabled:opacity-45 transition-colors"
      >
        {sending ? (
          <Loader2 size={11} className="animate-spin" />
        ) : notice?.tone === "success" ? (
          <Check size={11} className="text-[var(--green)]" />
        ) : (
          <Send size={11} />
        )}
        {notice?.tone === "success" ? notice.text : "Sync to Slack"}
      </button>
      <AnimatePresence>
        {notice?.tone === "error" && (
          <motion.div
            role="alert"
            initial={{ y: -8, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -8, opacity: 0 }}
            transition={{ type: "spring", stiffness: 320, damping: 32 }}
            className="absolute right-0 top-8 z-20 w-64 rounded-md border border-[color-mix(in_srgb,var(--red)_30%,var(--border))] bg-[var(--surface-elevated)] px-3 py-2 text-[10.5px] leading-relaxed text-[var(--red)]"
          >
            {notice.text}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
